import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { SlipType } from '../../bet-generation/slip-type';
import { getSku } from '../../picks/services/betslip-pick-helpers';
import { BetslipLinearTracking } from '../../tracking/models';
import { BetslipErrorTracking } from '../../tracking/tracking.models';
import {
    OVERASK_ACTIONS,
    OVERASK_CATEGORIES,
    OVERASK_EVENT_DETAILS,
    OVERASK_LABELS,
    OverAskTrackingLocation,
    TrackOveraskData,
    TrackingAcceptOfferLabelType,
    TrackingOfferType,
    TrackingPendingActionType,
    TrackingReviewRejectDialogActionType,
} from '../models/over-ask-tracking.model';

@Injectable()
export class OverAskTrackingService {
    constructor(
        private trackingService: TrackingService,
        private betslipConfig: BetslipConfig,
    ) {}

    trackPending(picks: BetslipPick[], slipType: SlipType, isLinear: boolean, betType: string): void {
        const type = this.getSlipType(slipType, picks.length > 1);

        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.load,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: OVERASK_EVENT_DETAILS.stakeLimitExceededNotice,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
            sku: this.getSku(picks),
        });
    }

    trackPendingClose(method: 'close x' | 'cancel', type: string, isLinear: boolean, betType: string): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.exit,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: method,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.exit,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPendingRejectPopup} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: method,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackReviewClose(method: 'close x' | 'cancel', isLinear: boolean): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.exit,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(isLinear),
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'not applicable',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: method,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackOddsChangeErrorOnOverAskBet() {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betslip',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'overask',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'not applicable',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'not applicable',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'your bet has not been placed info',
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackPendingRejectDialogLoad(type: string, isLinear: boolean, betType: string): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.load,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: OVERASK_EVENT_DETAILS.pendingConfirmationPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackPendingRejectDialogAction(action: TrackingPendingActionType, type: string, isLinear: boolean, betType: string): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPendingRejectPopup} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: action,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackReviewRejectDialogLoad(): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.stakeLimitExceed,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'not applicable',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: OverAskTrackingLocation.OverAskReviewRejectPopup,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: OVERASK_EVENT_DETAILS.reviewConfirmationPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackReviewRejectDialogAction(action: TrackingReviewRejectDialogActionType, type: string, isLinear: boolean, betType: string): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPendingRejectPopup} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: action,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
        });
    }

    trackRejectError(
        picks: BetslipPick[],
        error: 'trader denied' | 'offer expired' | 'bet placement failed' | 'ARC User Profile Restriction',
        betType: SlipType,
        isLinear: boolean,
        betsType: string,
    ): void {
        let action: string;
        const type = this.getSlipType(betType, picks.length > 1);
        if (error === 'bet placement failed') {
            action = OVERASK_ACTIONS.betError;
        } else {
            action = OVERASK_ACTIONS.betDenied;
        }

        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: action,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPendingRejectPopup} - ${this.getLocation(isLinear)}${betsType === '' ? '' : ' - ' + betsType}`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: error,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
            sku: this.getSku(picks),
        });
    }

    trackTraderOffer(picks: BetslipPick[], offer: 'new stake' | 'new odds' | 'new stake and new odds', isLinear: boolean): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.tradersOffer,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: offer,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: OVERASK_EVENT_DETAILS.alternativeOfferForCustomer,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
            sku: this.getSku(picks),
        });
    }

    trackTraderOfferExpired(picks: BetslipPick[], offer: TrackingOfferType, isLinear: boolean): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.tradersOfferExpired,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}`,
            [trackingConstants.COMPONENT_POSITION_EVENT]: offer,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: OVERASK_EVENT_DETAILS.alternativeOfferForCustomerExpired,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
            sku: this.getSku(picks),
        });
    }

    trackUserAcceptOffer(picks: BetslipPick[], action: TrackingAcceptOfferLabelType, isLinear: boolean, betType: string, type: string): void {
        this.track(trackingConstants.EVENT_OVERASK, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: OVERASK_CATEGORIES.overask,
            [trackingConstants.COMPONENT_LABEL_EVENT]: OVERASK_LABELS.fullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: OVERASK_ACTIONS.CTAclick,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${type} - triggered`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: `${OverAskTrackingLocation.OverAskPending} - ${this.getLocation(isLinear)}${betType === '' ? '' : ' - ' + betType}`,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: action,
            [trackingConstants.COMPONENT_URL_CLICKED]: 'not applicable',
            sku: this.getSku(picks),
        });
    }

    getSlipType(slipType: SlipType, isMultiPick: boolean): string {
        switch (slipType.key) {
            case 'single':
                if (isMultiPick) {
                    return 'multisingle';
                }

                return 'single';
            case 'combo':
                return 'combo';
        }

        return 'system';
    }

    track(eventName: string, data: TrackOveraskData): void {
        this.trackingService.track(eventName, data);
    }

    private getSku(picks: BetslipPick[]): string[] {
        return picks.map((betslipPick) => getSku(betslipPick));
    }

    private getLocation(isLinear: boolean): string {
        return isLinear
            ? this.betslipConfig.linearModeCheckboxesEnabled
                ? BetslipLinearTracking.LinearCheckbox
                : BetslipLinearTracking.LinearNoCheckbox
            : BetslipErrorTracking.TrackingLocationEvent;
    }
}
